import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useStyles } from './AppVerticalRouteMenu.styles';

export interface IVerticalRouteMenuItemConfig {
  label: string;
  path: string;
  additionalPaths?: string[];
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface IAppVerticalRouteMenuProps {
  config: IVerticalRouteMenuItemConfig[];
}

function normalizePath(path: string): string {
  const queryIndex = path.indexOf('?');
  const hashIndex = path.indexOf('#');

  if (queryIndex === -1 && hashIndex === -1) {
    return path.toLowerCase();
  }

  const cutAt = Math.min(
    queryIndex === -1 ? path.length : queryIndex,
    hashIndex === -1 ? path.length : hashIndex,
  );

  return path.slice(0, cutAt).toLowerCase();
}

function isItemActive(
  pathname: string,
  item: IVerticalRouteMenuItemConfig,
): boolean {
  const targetPath = normalizePath(item.path);
  const additional = item.additionalPaths?.map(normalizePath) ?? [];
  return targetPath === pathname || additional.includes(pathname);
}

const AppVerticalRouteMenu: React.FC<IAppVerticalRouteMenuProps> = ({
  config,
}) => {
  const { classes } = useStyles();
  const { navigate } = useNavigationStack();
  const location = useLocation();
  const pathname = normalizePath(location.pathname);

  const preparedConfig = useMemo(
    () =>
      config.map((item) => ({
        ...item,
        active: isItemActive(pathname, item),
      })),
    [config, pathname],
  );

  return (
    <List className={classes.root} disablePadding>
      {preparedConfig.map(({ label, path, icon: Icon, active }) => (
        <ListItem key={path} className={classes.listItem} disablePadding>
          <ListItemButton
            selected={active}
            className={classes.menuItem}
            onClick={() => navigate(path)}>
            <ListItemIcon className={classes.iconBadge}>
              <Icon className={classes.icon} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="text-md" className={classes.label}>
                  {label}
                </Typography>
              }
            />
            <KeyboardArrowRightRoundedIcon className={classes.chevron} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

export default AppVerticalRouteMenu;
